import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Alert, Button, SkeletonForm } from '@parsina/framework';
import { ClinicalI18n } from '@parsina/framework/clinical';
import { C, soft } from '@parsina/framework/tokens';

import { useTranslation, MIRROR_ICON } from '../../i18n';
import { STACK } from '../../constants/density';
import usePageTitle from '../../hooks/usePageTitle';
import { useClinicContext, BOOT, refOf } from '../../context/ClinicContext.jsx';
import { logout } from '../../lib/auth.js';
import AuthShell, { useAuthShellOffline } from '../../components/auth/AuthShell.jsx';
import { PrimaryAction } from '../../components/auth/OnboardingParts.jsx';
import MembershipSections from './MembershipSections.jsx';
import DoctorWelcome from './DoctorWelcome.jsx';
import { useResolvedAccountRegion } from '../Onboarding/accountRegion.js';

/**
 * «شروع» — the one screen for a person who is signed in and cannot enter a clinic.
 *
 * design §A4. v1 of this design had TWO screens here — `/no-access` for zero
 * memberships and a "waiting" state inside the inbox for pending-only — and named
 * one of them after a negative. They are the same moment: *you are signed in and
 * there is nothing to enter yet.* Merging them means a person whose invitation
 * arrives never has to discover that a different page now applies to them.
 *
 * Rendered OUTSIDE the shell, deliberately: the sidebar's whole job is to show the
 * active clinic and its nav, and there is no clinic. A shell with an empty sidebar
 * would be a worse answer than no shell.
 *
 * The body copy names the two legitimate reasons someone lands here with nothing —
 * they have not been added yet, or they were added under a different phone number.
 * The second is not hypothetical: `addStaffMember` matches phone numbers by exact
 * string against a realm that holds three different shapes (review G7), so a person
 * genuinely can have been added to an account that is not the one they just signed
 * in with. Their first move should be to check the number with their clinic, not to
 * create a duplicate clinic.
 *
 * ── Iranian accounts: «خوش آمدید!» ───────────────────────────────────────────
 *
 * For a number from Iran this screen IS registration: one کد نظام پزشکی field,
 * looked up as it is typed, and one press into a built clinic (DoctorWelcome,
 * Onboarding/quickSetup.js). «پزشک نیستم» under the field opens the one-field
 * clinic form. A number from abroad has no council to ask, so it keeps the two
 * ways into the wizard. Which side an account is on is not known on the first
 * frame, and the screen waits for it rather than flash the wrong one.
 *
 * There is no invitation code any more: a colleague is added by their number
 * at the clinic's «همکاران», and what they were added to waits here.
 *
 * ── It is also reachable from inside ────────────────────────────────────────
 *
 * Someone who already has a clinic gets here from the switcher. For them the
 * page is a detour, not a destination, so the header carries the way back — it
 * used to carry nothing, and the only exit was the browser's back button.
 */
export default function StartScreen() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const offline = useAuthShellOffline();
  const { state, memberships, enterable, reload } = useClinicContext();
  // null until the doctor BFF says where the number is from.
  const region = useResolvedAccountRegion();
  const iranian = Boolean(region) && !region.foreign;
  const [refreshing, setRefreshing] = React.useState(false);

  usePageTitle(t('clinic.start.title'));

  const suspended = memberships.filter((m) => m.blockedReason === 'SUSPENDED');
  const practitionerOnly = memberships.filter((m) => m.blockedReason === 'PRACTITIONER_NO_PANEL');
  const hasPending = memberships.some((m) => m.status === 'INVITED' || m.status === 'REQUESTED');
  const showSections = state === BOOT.PENDING_ONLY || hasPending;
  const backTo = enterable[0];

  const refresh = async () => {
    setRefreshing(true);
    try { await reload({ silent: true }); } finally { setRefreshing(false); }
  };

  /* A person who was just added at their clinic's desk should not have to sign
     out and back in to see it. It used to be a translucent button pinned to the
     corner of the gradient, which read as debug chrome; it belongs beside the
     list it refreshes. */
  const refreshButton = (
    <Button
      variant="ghost"
      onClick={refresh}
      disabled={refreshing || offline}
      aria-busy={refreshing || undefined}
      data-testid="start-refresh"
    >
      <RefreshCw
        size={14}
        aria-hidden="true"
        className={refreshing ? 'parsina-spin' : undefined}
      />
      {refreshing ? t('common.state.loading') : t('common.actions.refresh')}
    </Button>
  );

  // A number from abroad: the two ways into the wizard. «من پزشک هستم» first,
  // because it is the SUPERSET — it registers the clinic exactly as the button
  // below does and adds the practitioner before it, so a doctor who picks the
  // shorter path does not end up owning a clinic they are not a practitioner of.
  // The track travels as `?doctor=1` so a reload mid-registration stays on it.
  const wizardWays = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      }}>
        <h2 style={{ margin: 0, fontSize: '0.78125rem', fontWeight: 800, color: C.textPrimary }}>
          {t('clinic.start.waysIn')}
        </h2>
        {showSections ? null : refreshButton}
      </div>
      <PrimaryAction
        fullWidth
        onClick={() => navigate('/onboarding?doctor=1')}
        disabled={offline}
        data-testid="start-doctor"
      >
        {t('clinic.start.imDoctor')}
      </PrimaryAction>
      <PrimaryAction secondary fullWidth onClick={() => navigate('/onboarding')} disabled={offline} data-testid="start-create">
        {t('clinic.start.createClinic')}
      </PrimaryAction>
      <Button variant="link" onClick={() => logout()} data-testid="start-logout">
        {t('clinic.start.otherAccount')}
      </Button>
    </div>
  );

  const signOut = (
    <Button variant="link" onClick={() => logout()} data-testid="start-logout">
      {t('clinic.start.otherAccount')}
    </Button>
  );

  return (
    <AuthShell
      width="md"
      testId="start"
      title={t('clinic.start.title')}
      subtitle={t('nav.title.clinicPanel')}
      headerEnd={backTo ? (
        /* The kit's `onGradient` treatment, which only `IconButton` carries as
           a variant — this one needs a label beside the arrow, so the
           white-alpha is spelled out with `soft()`. */
        <Button
          variant="ghost"
          onClick={() => navigate(`/c/${refOf(backTo)}/`)}
          style={{ background: soft('#ffffff', 0.16), color: '#fff' }}
          data-testid="start-back"
        >
          <ArrowLeft size={15} aria-hidden="true" style={MIRROR_ICON} />
          {t('clinic.gate.goToOther', { name: backTo.clinicName })}
        </Button>
      ) : null}
      footer={!region ? null : (iranian ? signOut : wizardWays)}
    >
      {/* Abroad: the doctor wizard's gate — a 64px pale-blue tile, then the one
          paragraph that says where the person stands. In Iran the welcome form
          below says it instead, and only an invitation needs a line of its own. */}
      {region?.foreign ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
          <div
            aria-hidden="true"
            style={{
              width: 64, height: 64, borderRadius: 16, background: `linear-gradient(135deg, ${soft(C.navy, 0.06)}, ${soft(C.navy, 0.16)})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
            }}
          >
            <svg viewBox="0 0 24 24" fill={C.navyDark} width="32" height="32">
              <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
            </svg>
          </div>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: C.textSecondary, textAlign: 'center', lineHeight: 1.9, maxWidth: 380 }}>
            {hasPending ? t('clinic.start.pendingBody') : t('clinic.start.emptyBody')}
          </p>
        </div>
      ) : null}
      {iranian && hasPending ? (
        <p style={{ margin: `0 0 ${STACK.regular}px`, fontSize: '0.8125rem', color: C.textSecondary, lineHeight: 1.9 }}>
          {t('clinic.start.pendingBody')}
        </p>
      ) : null}

      {/* A suspension is NAMED. «شما عضو هیچ کلینیکی نیستید» to someone whose
          clinic suspended them is both false and an invitation to create a
          duplicate clinic (E12). */}
      {suspended.map((m) => (
        <Alert key={m.clinicId} variant="warning" style={{ marginBottom: STACK.regular }}>
          {t('clinic.start.suspendedAt', { name: m.clinicName })}
        </Alert>
      ))}
      {/* Silence about a practitioner membership is exactly what makes the
          doctor app's switcher misleading. Say it (E8). */}
      {practitionerOnly.map((m) => (
        <Alert key={m.clinicId} variant="info" style={{ marginBottom: STACK.regular }}>
          {t('clinic.start.practitionerAt', { name: m.clinicName })}
        </Alert>
      ))}

      {showSections ? (
        <div style={{ marginBottom: iranian ? 24 : 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            marginBottom: STACK.tight,
          }}>
            {refreshButton}
          </div>
          <MembershipSections disabled={offline} />
        </div>
      ) : null}

      {!region ? <SkeletonForm fields={1} actions={1} data-testid="start-loading" /> : null}
      {iranian ? (
        <ClinicalI18n language={locale} fallback={<SkeletonForm fields={1} actions={1} />}>
          <DoctorWelcome disabled={offline} />
        </ClinicalI18n>
      ) : null}

      {/* The refresh has no visible spinner of its own — the button says so —
          so the fact that something is happening is announced here. */}
      <span className="sr-only" aria-live="polite">
        {refreshing ? t('common.state.loading') : ''}
      </span>
    </AuthShell>
  );
}
